import React, { useEffect, useState } from 'react';
import { Modal, Card, Typography, Space, Spin, Empty } from 'antd';
import { ShopOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getRegistroTransmision } from '../../api/mejoraCU2Api';
import type { ReconciliacionTransmision } from '../../api/reconciliacionApi';
import { FctGlifoAtencion, fmtFecha } from '../../fct/react';

const { Text } = Typography;

/** Incidencia de bloqueo (E1/E2): código en Mono + glifo de atención, sin color de pastilla (R-11). */
const Incidencia: React.FC<{ codigo: string }> = ({ codigo }) => (
  <span>
    <span className="fct-codigo">{codigo}</span> <FctGlifoAtencion nivel="urgente" />
  </span>
);

interface Props {
  casoId: number | null;
  titulo?: string;
  open: boolean;
  onClose: () => void;
}

/** Popup de solo lectura con la información principal de la transmisión vinculada al caso. */
const RegistroTransmisionModal: React.FC<Props> = ({ casoId, titulo, open, onClose }) => {
  const [data, setData] = useState<ReconciliacionTransmision | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || casoId == null) return;
    let cancel = false;
    setLoading(true);
    setData(null);
    getRegistroTransmision(casoId)
      .then(r => { if (!cancel) setData(r); })
      .catch(() => { if (!cancel) setData(null); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, casoId]);

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={720} title={titulo ?? 'Transmisión vinculada'}>
      {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
      {!loading && !data && <Empty description="Sin datos del registro vinculado" />}
      {!loading && data && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">
              Transmisión <span className="fct-mono">#{data.id}</span> · patrón {data.patron}
              {data.fecha && <> · <span className="fct-mono">{fmtFecha(data.fecha)}</span></>}
            </Text>
          </div>

          <Card size="small" style={{ marginBottom: 12 }} title={<span><ShopOutlined style={{ marginRight: 8 }} />Farmacia</span>}>
            <div><Text strong>{data.farmacia?._Name || `ID ${data.farmaciaId}`}</Text></div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">ID Gescom: <span className="fct-mono">{data.farmaciaId}</span></Text>
              {data.farmacia?.provincia && <Text type="secondary"> · {data.farmacia.provincia}</Text>}
            </div>
          </Card>

          {data.vendedores?.length > 0 && (
            <Card
              size="small"
              style={{ marginBottom: 12 }}
              title={<span><ArrowRightOutlined style={{ marginRight: 8 }} />Titulares salientes (CT)</span>}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {data.vendedores.map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>{v.cliente?._Name || `ID ${v.vendedorId}`}</Text>
                    <Space size={6}>
                      {v.fechaFin && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{fmtFecha(v.fechaFin)}</Text>}
                      {v.excepcionE2 && <Incidencia codigo="E2" />}
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          )}

          {data.compradores?.length > 0 && (
            <Card
              size="small"
              title={<span><ArrowLeftOutlined style={{ marginRight: 8 }} />Titulares entrantes (AT)</span>}
            >
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {data.compradores.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>{c.cliente?._Name || `ID ${c.compradorId}`}</Text>
                    <Space size={6}>
                      {c.fechaInicio && <Text type="secondary" className="fct-mono" style={{ fontSize: 12 }}>{fmtFecha(c.fechaInicio)}</Text>}
                      {c.excepcionE1 && <Incidencia codigo="E1" />}
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          )}
        </>
      )}
    </Modal>
  );
};

export default RegistroTransmisionModal;
